// Built-in EXTRACTION signals — skill, memory, mcp_server, and tool.
//
// These are the same `run(event) -> list[tag]` contract as the security signals
// (exampleTriggers.js), but a different FAMILY: instead of flagging risk, they
// extract a durable, joinable annotation off each prompt — the skill body a
// `Skill` tool injected, the memory file an agent read/wrote, the MCP server's
// declared capability surface. Today the dashboard recomputes this from scratch,
// per-component, per view load (skillBodies.js / memoryBodies.js / mcpServerDefs.js / toolDefs.js
// called inline in the detail lenses). Re-expressed as signals, the extraction
// runs ONCE per event in the same scan as the security signals, materializes into
// the per-trace annotation store, and — when the backend signal pipeline grows the
// normalized `turns` + `tool_defs` surface (Issue 2 / O-2148) — runs server-side
// unchanged and persists to `signal_tags`, so the skill/memory/MCP graph becomes
// queryable off the analytics graph instead of window-bound in the browser.
//
// INPUT: the normalized `event.turns` — `[{role, text, blocks}]`, where `blocks`
// is the provider-agnostic block vocabulary parseApiRequest produces (tool_use
// {id,name,input} / tool_result {tool_use_id,content,isError}). The skill/memory
// kernels pair tool_use↔tool_result by id across the whole transcript, exactly as
// the JS extractors do — so a kernel never matches a per-LLM-provider shape. The
// mcp_server kernel additionally reads `event.tool_defs` (the offered-tools
// surface) and the `role:"system"` turn.
//
// PORTED FROM (kept behavior-identical; see builtinExtractors.parity.test.js):
//   • skill       ← extractSkillBodies        (skillBodies.js)
//   • memory      ← extractMemoryBodies        (memoryBodies.js)
//                  + extractMemoryFromPath     (memoryNames.js)
//                  + cleanReadBody             (memoryBodies.js)
//                  + extractMemoryRefs         (memoryLinks.js)
//   • mcp_server  ← extractServerSurface / surfaceFingerprint /
//                    extractServerInstructions (mcpServerDefs.js)
//                  + extractServerFromToolName / shortToolName /
//                    labelForMcpServer         (mcpServerNames.js)
//   • tool        ← extractToolDefs            (toolDefs.js)   [sibling of mcp_server, not built on it]
//
// `value` carries the FULL body inline (decision: full-body-inline). For memory
// the structured detail (op / body / refs / project) is JSON-encoded into the
// scalar `value`, per the contract ("JSON-encode structured detail"); mcp_server
// likewise JSON-encodes {surface, instructions, fingerprint}. Refs/fingerprints
// are derived reductions, so the graph's edges/drift become a downstream reduction
// over emitted tags rather than a re-parse.

// A converged extractor: one python step whose source is the byte-portable
// `run(event)` module — same kernel shape as signalKernel in exampleTriggers.js.
// `family` is the tag namespace it emits ("skill" | "memory" | "mcp_server");
// these are NOT security alerts, so the signals console filters them out
// (registration step).
const extractorKernel = (id, command, title, description, source, family) => ({
    id,
    command,
    title,
    description,
    family,
    renderSource: "signal",
    steps: [{ kind: "python", id: "signal", entry: "run", code: source }],
});

// ── skill ───────────────────────────────────────────────────────────
// A skill's injected SKILL.md body is the tool_result linked to a `Skill`
// tool_use (input.skill names it) — or to any tool_use that Read the
// `…/skills/<name>/SKILL.md` file directly. One tag per (skill, body).
export const skillExtractor = extractorKernel(
    "extract-skill",
    "skill",
    "Skill body extraction",
    "Records skill loads from Skill/skill calls or reads of skills/<name>/SKILL.md. Captured instruction content is returned as the value; a Copilot load acknowledgement records usage with an empty value. Annotates the endpoint.",
    `
import re

_SKILL_MD_PATH_RE = re.compile(r'skills[/\\\\]([^/"\\\\]+)[/\\\\]SKILL\\.md')

def _result_text(content):
    if isinstance(content, str):
        return content
    if isinstance(content, list):
        parts = []
        for b in content:
            if isinstance(b, dict) and b.get("type") == "text" and isinstance(b.get("text"), str):
                parts.append(b["text"])
        return "\\n".join(parts)
    return ""

def _tool_use_skill(b):
    if not isinstance(b, dict) or b.get("type") != "tool_use":
        return None
    name = b.get("name")
    inp = b.get("input") if isinstance(b.get("input"), dict) else {}
    if name in ("Skill", "skill") and isinstance(inp.get("skill"), str):
        return inp.get("skill")
    # A direct Read of the SKILL.md file loads its body too — match the path in a
    # file-path input (file_path / filePath / path / notebook_path) ONLY, not anywhere in the
    # input, so a tool that merely references the path in its args (a Bash command)
    # isn't mistaken for loading the skill (capturing its output as the body).
    for key in ("file_path", "filePath", "path", "notebook_path"):
        if key == "filePath" and name != "read_file":
            continue
        p = inp.get(key)
        if isinstance(p, str):
            m = _SKILL_MD_PATH_RE.search(p)
            if m:
                return m.group(1)
    return None

def _turns(event):
    t = event.get("turns") if isinstance(event, dict) else None
    return t if isinstance(t, list) else []

def _blocks(turn):
    if not isinstance(turn, dict):
        return []
    b = turn.get("blocks")
    return b if isinstance(b, list) else []

# A line that announces a filesystem path (Windows drive, POSIX-absolute, or ~).
# Some harnesses prefix the injected body with one such line + a blank line
# (e.g. "Base directory for this skill: <path>"); strip it so drift keys on the
# body, not the per-machine install path.
_PATH_LINE_RE = re.compile(r'[A-Za-z]:[\\\\/]|/\\w[\\w.+-]*/|~[\\\\/]')

def _strip_path_preamble(text):
    m = re.search(r'\\r?\\n\\r?\\n', text)
    if not m:
        return text
    head = text[:m.start()]
    if "\\n" not in head and head[:1] not in "#-*>|=~" and _PATH_LINE_RE.search(head):
        return text[m.end():]
    return text

def _injected_skill_body(blocks, idx, consumed):
    # First UNCLAIMED text block after the launch ack in this turn — the injected
    # body. Parallel tools' results may sit between, so scan forward; \`consumed\`
    # tracks claimed text blocks so two launches in one turn each get their own
    # body (not both the first), keeping the per-skill drift fingerprint honest.
    for j in range(idx + 1, len(blocks)):
        if j in consumed:
            continue
        nb = blocks[j]
        if isinstance(nb, dict) and nb.get("type") == "text" and isinstance(nb.get("text"), str) and nb["text"]:
            consumed.add(j)
            return _strip_path_preamble(nb["text"])
    return None

def run(event):
    skill_by_use_id = {}
    copilot_skill_use_ids = set()
    for turn in _turns(event):
        for b in _blocks(turn):
            context = b.get("call_context") if isinstance(b, dict) and b.get("type") == "tool_result" else None
            if isinstance(context, dict) and context.get("id") == b.get("tool_use_id"):
                skill = _tool_use_skill(context)
                if skill and context.get("id"):
                    skill_by_use_id[context["id"]] = skill
                    if context.get("name") == "skill":
                        copilot_skill_use_ids.add(context["id"])
            skill = _tool_use_skill(b)
            if skill and b.get("id"):
                skill_by_use_id[b.get("id")] = skill
                if b.get("name") == "skill":
                    copilot_skill_use_ids.add(b["id"])
    if not skill_by_use_id:
        return []
    tags = []
    for turn in _turns(event):
        blocks = _blocks(turn)
        consumed = set()
        for idx, b in enumerate(blocks):
            if isinstance(b, dict) and b.get("type") == "tool_result" and b.get("tool_use_id") in skill_by_use_id:
                if b.get("is_error") or b.get("isError"):
                    continue
                body = _result_text(b.get("content"))
                if b.get("tool_use_id") in copilot_skill_use_ids:
                    name = skill_by_use_id[b["tool_use_id"]]
                    acknowledgement = (
                        f'Skill "{name}" loaded successfully. '
                        "Follow the instructions in the skill context."
                    )
                    if body.strip() == acknowledgement:
                        tags.append({
                            "subject_type": "endpoint",
                            "namespace": "skill",
                            "tag": name,
                            "value": "",
                        })
                        continue
                    if "\\n" not in body.strip():
                        continue
                # A single-line result is a launch ack, not a body; the real
                # SKILL.md — file Read or injected — is always multi-line. Recover
                # the body from the text block injected after the ack.
                if "\\n" not in body.strip():
                    injected = _injected_skill_body(blocks, idx, consumed)
                    if injected:
                        body = injected
                if body:
                    tags.append({
                        "subject_type": "endpoint",
                        "namespace": "skill",
                        "tag": skill_by_use_id.get(b.get("tool_use_id")),
                        "value": body,
                    })
    return tags
`,
    "skill",
);

// ── memory ──────────────────────────────────────────────────────────
// A memory's content surfaces as the Read/Write of `…/.claude/.../memory/<slug>.md`:
// Write carries the body inline on input.content; Read defers to its tool_result.
// One tag per (slug, op); value = JSON {op, body, refs, project}.
export const memoryExtractor = extractorKernel(
    "extract-memory",
    "memory",
    "Memory body extraction",
    "Extracts the content of each agent memory file touched in the transcript — `…/.claude/.../memory/<slug>.md` (Claude Code singular form). Write carries the body inline; Read's body comes back in the paired tool_result (cat -n gutter + staleness preamble stripped). One `memory.<slug>` tag per touch, value = JSON {op, body, refs, project} where refs are the memory's wikilink/pointer targets. Annotates the endpoint.",
    `
import re, json, math

_MD_RE = re.compile(r'\\.md$', re.I)
_ALNUM_RE = re.compile(r'[a-z0-9]', re.I)
_MEMORY_SEG_RE = re.compile(r'/memory/', re.I)

# ── memoryNames.extractMemoryFromPath ──
def _extract_memory_from_path(path):
    norm = str(path if path is not None else "").replace("\\\\", "/")
    if not _MEMORY_SEG_RE.search(norm):
        return None
    segs = [s for s in norm.split("/") if s]
    i = -1
    for idx, seg in enumerate(segs):
        if seg.lower() == "memory":
            i = idx
            break
    if i == -1:
        return None
    if not any(seg == ".claude" for seg in segs[:i]):
        return None
    if i + 1 != len(segs) - 1:
        return None
    leaf = segs[i + 1] if (i + 1) < len(segs) else ""
    if not leaf or not _MD_RE.search(leaf):
        return None
    slug = _MD_RE.sub("", leaf)
    if not _ALNUM_RE.search(slug) or ("," in slug):
        return None
    return slug

# ── memoryNames.memoryProjectDirFromPath (normalized to be Windows-safe) ──
def _memory_project_dir(path):
    s = str(path if path is not None else "").replace("\\\\", "/")
    i = s.rfind("/memory/")
    return None if i == -1 else s[:i]

# ── memoryBodies.cleanReadBody ──
_SYSREMINDER_RE = re.compile(r'^\\s*<system-reminder>[\\s\\S]*?</system-reminder>\\s*', re.I)
_GUTTER_FORMS = [re.compile(r'^\\s*\\d+\\t'), re.compile(r'^\\s*\\d+→'), re.compile(r'^\\s*\\d+ {2,}')]

def _clean_read_body(text):
    s = str(text if text is not None else "")
    # JS: /^\\ufeff?\\s*<system-reminder>...<\\/system-reminder>\\s*/i — optional BOM
    # then the reminder; only strips when a reminder is actually present.
    prefix = "\\ufeff" if s.startswith("\\ufeff") else ""
    rest = s[len(prefix):]
    m = _SYSREMINDER_RE.match(rest)
    if m:
        s = rest[m.end():]
    lines = s.split("\\n")
    non_blank = [l for l in lines if l.strip() != ""]
    if not non_blank:
        return s
    best = None
    best_hits = 0
    for rx in _GUTTER_FORMS:
        hits = sum(1 for l in non_blank if rx.match(l))
        if hits > best_hits:
            best_hits = hits
            best = rx
    if best is not None and best_hits >= math.ceil(len(non_blank) * 0.6):
        s = "\\n".join(best.sub("", l, count=1) for l in lines)
    return s

# ── memoryLinks.extractMemoryRefs ──
_MD_LINK_RE = re.compile(r'\\[[^\\]]*\\]\\(([^)]+)\\)')
_WIKI_RE = re.compile(r'\\[\\[([^\\]|]+)(?:\\|[^\\]]*)?\\]\\]')
_HREF_DISQUALIFY_RE = re.compile(r'[/\\\\:?#]')
_SLUG_MD_RE = re.compile(r'^([A-Za-z0-9][A-Za-z0-9._-]*)\\.md$')
_WIKI_DISQUALIFY_RE = re.compile(r'[/\\\\:?#\\[\\]|]')

def _memory_slug_from_href(href):
    h = str(href if href is not None else "").strip()
    if not h or _HREF_DISQUALIFY_RE.search(h):
        return None
    m = _SLUG_MD_RE.match(h)
    if not m:
        return None
    slug = m.group(1)
    return slug if _ALNUM_RE.search(slug) else None

def _slug_from_wiki_target(raw):
    s = _MD_RE.sub("", str(raw if raw is not None else "").strip())
    if not s or _WIKI_DISQUALIFY_RE.search(s) or not _ALNUM_RE.search(s):
        return None
    return s

def _extract_memory_refs(text):
    out = []
    seen = set()
    body = str(text if text is not None else "")
    for m in _MD_LINK_RE.finditer(body):
        slug = _memory_slug_from_href(m.group(1))
        if slug and slug not in seen:
            seen.add(slug); out.append(slug)
    for m in _WIKI_RE.finditer(body):
        slug = _slug_from_wiki_target(m.group(1))
        if slug and slug not in seen:
            seen.add(slug); out.append(slug)
    return out

# ── memoryBodies.resultText / toolUseMemory / extractMemoryBodies ──
def _result_text(content):
    if isinstance(content, str):
        return content
    if isinstance(content, list):
        parts = []
        for b in content:
            if isinstance(b, dict) and b.get("type") == "text" and isinstance(b.get("text"), str):
                parts.append(b["text"])
        return "\\n".join(parts)
    return ""

_TOOL_USE_ERROR_RE = re.compile(r'^\\s*<tool_use_error>')

def _write_content(inp):
    # Full-body authoring — a Write's content or the memory tool's file_text.
    for k in ("content", "file_text"):
        v = inp.get(k)
        if isinstance(v, str):
            return v
    return None

def _edit_hunks(inp):
    # Partial edits — capture every (old, new) hunk so the whole change (the diff)
    # is preserved, instead of the tool's "…has been updated" confirmation stub.
    # Covers Edit (old_string/new_string), MultiEdit (edits[]), str_replace
    # (old_str/new_str), and the memory tool's insert (insert_text).
    def _pair(d):
        old = d.get("old_string")
        if not isinstance(old, str):
            old = d.get("old_str") if isinstance(d.get("old_str"), str) else None
        new = d.get("new_string")
        if not isinstance(new, str):
            new = d.get("new_str") if isinstance(d.get("new_str"), str) else None
        if new is None and isinstance(d.get("insert_text"), str):
            new = d.get("insert_text")
        return {"old": old, "new": new} if (old is not None or new is not None) else None
    hunks = []
    multi = inp.get("edits")
    if isinstance(multi, list):
        for it in multi:
            if isinstance(it, dict):
                p = _pair(it)
                if p:
                    hunks.append(p)
    else:
        p = _pair(inp)
        if p:
            hunks.append(p)
    return hunks or None

def _tool_use_memory(b):
    if not isinstance(b, dict) or b.get("type") != "tool_use":
        return None
    inp = b.get("input")
    if not isinstance(inp, dict):
        inp = {}
    path = inp.get("file_path")
    if path is None:
        path = inp.get("path")
    if path is None:
        path = inp.get("notebook_path")
    slug = _extract_memory_from_path(path)
    if not slug:
        return None
    project = _memory_project_dir(path)
    content = _write_content(inp)
    if content is not None:
        return {"slug": slug, "body": content, "op": "write", "project": project}
    hunks = _edit_hunks(inp)
    if hunks is not None:
        # body = the new text across hunks (what the memory now says there); the
        # full before/after of every hunk is preserved in edits. The Edit tool's
        # own tool_result is a confirmation stub, so it is NOT paired here.
        body = "\\n".join(h["new"] for h in hunks if isinstance(h.get("new"), str))
        return {"slug": slug, "body": body, "op": "edit", "edits": hunks, "project": project}
    return {"slug": slug, "body": None, "op": "read", "useId": b.get("id"), "project": project}

def _turns(event):
    t = event.get("turns") if isinstance(event, dict) else None
    return t if isinstance(t, list) else []

def _blocks(turn):
    if not isinstance(turn, dict):
        return []
    b = turn.get("blocks")
    return b if isinstance(b, list) else []

def _extract_memory_bodies(event):
    out = []
    read_by_use_id = {}
    for turn in _turns(event):
        for b in _blocks(turn):
            hit = _tool_use_memory(b)
            if not hit:
                continue
            if hit.get("body") is not None:
                out.append(hit)
            elif hit.get("useId"):
                read_by_use_id[hit["useId"]] = hit
    if read_by_use_id:
        for turn in _turns(event):
            for b in _blocks(turn):
                if isinstance(b, dict) and b.get("type") == "tool_result" and b.get("tool_use_id") in read_by_use_id:
                    if b.get("isError"):
                        continue
                    body = _result_text(b.get("content"))
                    if body and not _TOOL_USE_ERROR_RE.match(body):
                        src = read_by_use_id[b.get("tool_use_id")]
                        out.append({"slug": src["slug"], "body": _clean_read_body(body), "op": "read", "project": src.get("project")})
    return out

def run(event):
    tags = []
    for e in _extract_memory_bodies(event):
        body = e.get("body") or ""
        val = {
            "op": e.get("op"),
            "body": body,
            "refs": _extract_memory_refs(body),
            "project": e.get("project"),
        }
        if e.get("edits") is not None:
            val["edits"] = e.get("edits")   # full (old, new) diff for edit ops
        value = json.dumps(val, ensure_ascii=False)
        tags.append({
            "subject_type": "endpoint",
            "namespace": "memory",
            "tag": e.get("slug"),
            "value": value,
        })
    return tags
`,
    "memory",
);

// ── mcp_server ──────────────────────────────────────────────────────
// An MCP server's CAPABILITY SURFACE in one request: the set of
// `mcp__<server>__<tool>` tool DEFINITIONS the model was offered (name + short
// tool + description + schema), plus any server-level instruction prose found in
// the system prompt, plus a stable FINGERPRINT of the surface. One
// `mcp_server.<server>` tag per server present in the request; value = JSON
// {surface, instructions, fingerprint}. Equal fingerprints across a window = the
// same surface; differing ones = DRIFT (a tool added/removed or a
// description/schema changed — version skew across endpoints, or tool-poisoning /
// rug-pull tampering). The kernel emits the fingerprint so the materialized
// annotation IS the drift key: the consumer groups by it, and a backend running
// this kernel makes "did server X drift" a GROUP BY over signal_tags rather than
// a re-fetch-and-re-fingerprint of every prompt. The cross-event grouping itself
// (counting prompts per fingerprint → variants) is per-window and stays
// downstream — the kernel is per-event.
//
// PORTED FROM (kept behavior-identical; see builtinExtractors.parity.test.js):
//   • surface       ← extractServerSurface          (mcpServerDefs.js)
//   • fingerprint   ← surfaceFingerprint (+ normWs / stableJson)   (mcpServerDefs.js)
//   • instructions  ← extractServerInstructions     (mcpServerDefs.js)
//   • server/tool   ← extractServerFromToolName / shortToolName / labelForMcpServer
//                                                    (mcpServerNames.js)
//
// The kernel discovers opening-request capability surfaces from `event.tool_defs` (the signal contract's
// offered-tools surface — `{name, description, schema_text}`, the tool-poisoning
// channel). A server is offered with its tool defs and its instructions in the
// SAME request, so discovery from tool_defs reaches every server whose surface OR
// instructions we'd want; an instructions-only server with zero offered tools
// (not a real MCP injection shape) is intentionally out of scope. Schema rides as
// the contract's flattened `schema_text` — canonicalized in-kernel for the
// fingerprint, and re-parsed by the consumer for the tool-definition panel.
// Current canonical MCP calls also emit usage-only tags with an empty value;
// unavailable definitions do not become a partial surface or a drift fingerprint.
export const mcpServerExtractor = extractorKernel(
    "extract-mcp-server",
    "mcp_server",
    "MCP server surface extraction",
    "Records MCP servers used in the current tool exchange. Opening requests also capture declared capabilities and instructions when offered definitions carry server identity. Usage without definitions has an empty value; complete definitions carry JSON {surface, instructions, fingerprint}. Different complete fingerprints indicate definition drift. Annotates the endpoint.",
    `
import re, json

# mcpServerNames.PRETTY_LABELS — only consulted for the instruction-block weak
# fallback (the label words). The \\u00b7 keeps the source ASCII; it never reaches
# the emitted value.
_PRETTY_LABELS = {
    "claude_ai_Linear": "claude.ai \\u00b7 Linear",
    "claude_ai_Gmail": "claude.ai \\u00b7 Gmail",
    "claude_ai_Google_Calendar": "claude.ai \\u00b7 Google Calendar",
    "claude_ai_Google_Drive": "claude.ai \\u00b7 Google Drive",
    "claude_ai_Slack": "claude.ai \\u00b7 Slack",
    "claude_ai_origin-staging": "claude.ai \\u00b7 origin-staging",
    "plugin_chrome-devtools-mcp_chrome-devtools": "Chrome DevTools",
}

# mcpServerNames.extractServerFromToolName — \`<server>\` from \`mcp__<server>__<rest>\`.
def _server_from_tool_name(name):
    if not isinstance(name, str):
        return None
    if not name.startswith("mcp__"):
        return None
    parts = name.split("__")
    if len(parts) < 3:
        return None
    return parts[1] or None

# mcpServerNames.shortToolName — \`mcp__<server>__<tool>\` → \`<tool>\`.
def _short_tool_name(name):
    s = name if isinstance(name, str) else ""
    parts = s.split("__")
    return "__".join(parts[2:]) if len(parts) >= 3 else s

# mcpServerNames.labelForMcpServer — display label, underscore-to-space fallback.
def _label_for_server(server):
    if not server:
        return ""
    if server in _PRETTY_LABELS:
        return _PRETTY_LABELS[server]
    label = str(server)
    if not re.search(r'%(?![0-9A-Fa-f]{2})', label):
        try:
            label = re.sub(r'(?:%[0-9A-Fa-f]{2})+', lambda m: bytes.fromhex(m.group(0).replace("%", "")).decode("utf-8"), label)
        except UnicodeDecodeError:
            label = str(server)
    return label.replace("_", " ")

def _turns(event):
    t = event.get("turns") if isinstance(event, dict) else None
    return t if isinstance(t, list) else []

def _tool_defs(event):
    d = event.get("tool_defs") if isinstance(event, dict) else None
    return d if isinstance(d, list) else []

# mcpServerDefs.systemText — the system-prompt text turns joined.
def _system_text(turns):
    parts = []
    for t in turns:
        if not isinstance(t, dict) or t.get("role") != "system":
            continue
        blocks = t.get("blocks")
        for b in (blocks if isinstance(blocks, list) else []):
            if isinstance(b, dict) and b.get("type") == "text" and isinstance(b.get("text"), str) and b.get("text"):
                parts.append(b["text"])
    return "\\n\\n".join(parts)

# mcpServerDefs.extractServerSurface — the server's offered tools in one request.
# schema rides as the contract's schema_text (the consumer re-parses it).
def _server_surface(tool_defs, server):
    out = []
    for t in tool_defs:
        if not isinstance(t, dict):
            continue
        name = t.get("name")
        if _server_from_tool_name(name) != server:
            continue
        desc = t.get("description")
        schema_text = t.get("schema_text")
        out.append({
            "name": name,
            "tool": _short_tool_name(name),
            "description": desc if isinstance(desc, str) else "",
            "schema_text": schema_text if isinstance(schema_text, str) else "",
        })
    out.sort(key=lambda x: x["name"])
    return out

# mcpServerDefs.surfaceFingerprint (+ normWs / stableJson) — the DRIFT KEY for a
# surface: sorted (name, normalized description, canonical-JSON schema) tuples
# joined with separators that can't occur in the payload. Computed IN the kernel
# (not recomputed by the consumer) so the materialized annotation carries the
# drift key directly — when the backend runs this kernel the key lands in
# signal_tags and "did server X's surface drift" becomes a GROUP BY, not a
# re-fetch-and-re-fingerprint. Per-event by construction; the cross-event
# grouping of equal fingerprints into variants stays a downstream reduction.
def _norm_ws(s):
    return re.sub(r'\\s+', ' ', str(s if s is not None else "")).strip()

# Canonical JSON with object keys sorted at every level (key order is
# serialization noise, not drift). Mirrors mcpServerDefs.stableJson; numbers /
# strings / bools round-trip through schema_text's JS serialization, so json
# echoes them identically. null/absent schema → "" (matches stableJson(null)).
def _stable_json(v):
    if v is None:
        return ""
    if isinstance(v, dict):
        return "{" + ",".join(json.dumps(k, ensure_ascii=False) + ":" + _stable_json(v[k]) for k in sorted(v.keys())) + "}"
    if isinstance(v, list):
        return "[" + ",".join(_stable_json(x) for x in v) + "]"
    return json.dumps(v, ensure_ascii=False)

def _stable_json_from_text(schema_text):
    if not schema_text:
        return ""
    try:
        return _stable_json(json.loads(schema_text))
    except Exception:
        return ""

def _surface_fingerprint(surface):
    return "\\u241e".join(
        t["name"] + "\\u241f" + _norm_ws(t.get("description")) + "\\u241f" + _stable_json_from_text(t.get("schema_text"))
        for t in surface
    )

_INSTRUCTION_CAP = 6000

# mcpServerDefs.extractServerInstructions — the system-prompt paragraphs that
# reference this server. Distinctive anchors (id / mcp__id__ / full tool names)
# first; label words only as a fallback. Returns None when nothing matches.
def _server_instructions(turns, server, tool_names):
    sys = _system_text(turns)
    if not sys:
        return None
    sid = str(server or "")
    strong = set()
    if sid:
        strong.add(sid.lower())
        strong.add(("mcp__" + sid + "__").lower())
    for n in tool_names:
        if n:
            strong.add(str(n).lower())
    weak = set()
    for w in re.split(r'[\\s\\u00b7]+', _label_for_server(sid)):
        if len(w) >= 4:
            weak.add(w.lower())
    paras = [p.strip() for p in re.split(r'\\n{2,}', sys)]
    paras = [p for p in paras if p]
    def pick(toks):
        if not toks:
            return []
        hits = []
        for p in paras:
            low = p.lower()
            for t in toks:
                if t in low:
                    hits.append(p)
                    break
        return hits
    matched = pick(list(strong))
    if not matched:
        matched = pick(list(weak))
    if not matched:
        return None
    text = "\\n\\n".join(matched).strip()
    if len(text) > _INSTRUCTION_CAP:
        return text[:_INSTRUCTION_CAP] + "\\n\\u2026"
    return text

def _current_mcp_servers(turns, execution=None):
    start = execution.get("current_exchange_start") if isinstance(execution, dict) else None
    if not isinstance(start, int) or isinstance(start, bool) or start < 0:
        start = 0
        for i in range(len(turns) - 1, -1, -1):
            turn = turns[i]
            if not isinstance(turn, dict) or turn.get("role") != "user":
                continue
            blocks = turn.get("blocks")
            # Tool results, including injected companion blocks, continue the
            # exchange. Other user messages bound it even when empty or unknown;
            # assistant messages can contain parallel calls or trailing text.
            if isinstance(blocks, list) and any(
                    isinstance(block, dict) and block.get("type") == "tool_result"
                    for block in blocks):
                continue
            start = i + 1
            break
    servers = []
    seen = set()
    for turn in turns[start:]:
        blocks = turn.get("blocks") if isinstance(turn, dict) else None
        for block in blocks if isinstance(blocks, list) else []:
            if not isinstance(block, dict):
                continue
            name = None
            if block.get("type") == "tool_use":
                name = block.get("name")
            elif block.get("type") == "tool_result":
                name = block.get("tool_name") or block.get("toolName")
                context = block.get("call_context")
                call_id = block.get("tool_use_id")
                if (isinstance(call_id, str) and call_id and isinstance(context, dict)
                        and context.get("id") == call_id and _server_from_tool_name(context.get("name"))):
                    name = context.get("name") or name
            server = _server_from_tool_name(name)
            if server and server not in seen:
                # Scan the whole exchange while retaining only its 128 smallest
                # IDs, so large catalogs cannot grow this set with input size.
                if len(servers) == 128:
                    if server >= servers[-1]:
                        continue
                    seen.remove(servers.pop())
                low, high = 0, len(servers)
                while low < high:
                    middle = (low + high) // 2
                    if servers[middle] < server:
                        low = middle + 1
                    else:
                        high = middle
                servers.insert(low, server)
                seen.add(server)
    return servers

def run(event):
    turns = _turns(event)
    tool_defs = [] if any(isinstance(t, dict) and t.get("role") == "assistant" for t in turns) else _tool_defs(event)
    servers = []
    seen = set()
    for t in tool_defs:
        if not isinstance(t, dict):
            continue
        s = _server_from_tool_name(t.get("name"))
        if s and s not in seen:
            seen.add(s)
            servers.append(s)
    servers.sort()
    tags = []
    for server in servers:
        surface = _server_surface(tool_defs, server)
        if not surface:
            continue
        instructions = _server_instructions(turns, server, [t["name"] for t in surface])
        value = json.dumps({
            "surface": surface,
            "instructions": instructions,
            "fingerprint": _surface_fingerprint(surface),
        }, ensure_ascii=False)
        tags.append({
            "subject_type": "endpoint",
            "namespace": "mcp_server",
            "tag": server,
            "value": value,
        })
    execution = event.get("_signal_execution") if isinstance(event, dict) else None
    for server in _current_mcp_servers(turns, execution):
        if server not in seen:
            tags.append({
                "subject_type": "endpoint",
                "namespace": "mcp_server",
                "tag": server,
                "value": "",
            })
    return tags
`,
    "mcp_server",
);

// ── tool ─────────────────────────────────────────────────────────────
// The per-tool analog of mcpServerExtractor — EVERY offered tool (built-in AND
// MCP, no mcp__ filter) gets its declared definition + a drift fingerprint. The
// Python body mirrors backend/signals-catalog/tool/signal.py; the JS original is
// extractToolDefs (mcpServerDefs.js), parity-tested. Reads the flattened
// tool_defs (name, description, schema_text) hydratePrompt builds.
export const toolExtractor = extractorKernel(
    "extract-tool",
    "tool",
    "Tool definition extraction",
    "Extracts the declared definition of every tool the model was offered (built-in AND MCP): name, description, and input schema, plus a stable fingerprint. One `tool.<name>` tag per distinct tool, value = JSON {name, description, schema}; a `tool.fingerprint` facet carries the drift digest. Equal fingerprints across prompts = same definition; differing ones = drift (version skew or tampering). The per-tool analog of mcp_server, covering all tools. Annotates the endpoint.",
    `
import re, json

def _content_hash(text):
    h = 0xCBF29CE484222325
    for byte in str(text if text is not None else "").encode("utf-8"):
        h = ((h ^ byte) * 0x100000001B3) & 0xFFFFFFFFFFFFFFFF
    return format(h, "016x")

def _norm_ws(s):
    return re.sub(r'\\s+', ' ', str(s if s is not None else "")).strip()

# Canonical JSON with object keys sorted at every level (key order is
# serialization noise, not drift). Mirrors mcpServerDefs.stableJson; null schema
# → "" so a schema-less tool has a stable, non-drifting fingerprint.
def _stable_json(v):
    if v is None:
        return ""
    if isinstance(v, dict):
        return "{" + ",".join(json.dumps(k, ensure_ascii=False) + ":" + _stable_json(v[k]) for k in sorted(v.keys())) + "}"
    if isinstance(v, list):
        return "[" + ",".join(_stable_json(x) for x in v) + "]"
    return json.dumps(v, ensure_ascii=False)

# The contract flattens each tool def's schema to schema_text (a JSON string);
# re-parse it to the object the instance value + fingerprint use.
def _schema_obj(schema_text):
    if not schema_text:
        return None
    try:
        return json.loads(schema_text)
    except Exception:
        return None

def _tool_defs(event):
    d = event.get("tool_defs") if isinstance(event, dict) else None
    return d if isinstance(d, list) else []

def run(event):
    tags = []
    seen = set()
    for t in _tool_defs(event):
        if not isinstance(t, dict):
            continue
        name = t.get("name")
        if not isinstance(name, str) or not name or name in seen:
            continue
        seen.add(name)
        desc = t.get("description") if isinstance(t.get("description"), str) else ""
        schema = _schema_obj(t.get("schema_text") if isinstance(t.get("schema_text"), str) else "")
        tags.append({
            "subject_type": "endpoint",
            "namespace": "tool",
            "tag": name,
            "value": json.dumps({"name": name, "description": desc, "schema": schema}, ensure_ascii=False),
        })
        tags.append({
            "subject_type": "endpoint",
            "namespace": "tool.fingerprint",
            "tag": name,
            "value": _content_hash(_norm_ws(desc) + "\\u241f" + _stable_json(schema)),
        })
    return tags
`,
    "tool",
);

// The shipped extraction set, mirroring EXAMPLE_TRIGGERS' shape so triggerStore
// can merge it into the read-only built-ins (registration step). Kept separate
// from EXAMPLE_TRIGGERS (the security base set) so the two families stay legible.
export const BUILTIN_EXTRACTORS = [skillExtractor, memoryExtractor, mcpServerExtractor, toolExtractor];
